import { RiCheckboxCircleFill, RiVerifiedBadgeLine } from "@remixicon/react";
import classNames from "classnames";
import React, {
    ReactElement, useEffect,
    useState,
} from "react";
import { Button, ButtonGroup } from "react-bootstrap";
import Confetti from "react-confetti";
import { PortalBillingTierDuration, useCreateBillingOrderMutation, useGetBillingQuery } from "../../../../__generated__/api";
import { LoadingIcon } from "../../../../components/loading/Loading";
import Popover from "../../../../components/popover/Popover";
import FormGroup from "../../../../components/theme/form-group/FormGroup";
import useArticleContext from "../../../../contexts/HelpContext";

/* eslint-disable @typescript-eslint/no-namespace */
declare global {
    namespace JSX {
        interface IntrinsicElements {
            "stripe-pricing-table": React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
        }
    }
}
/* eslint-enable @typescript-eslint/no-namespace */

export function BillingSuccess(): ReactElement {
    return (
        <div className="full-page">
            <div className="centered-container centered-container-lg pt-5 align-items-center">

                <h4 className="text-center w-100 mb-4">
                    🎉 Thanks for signing up!
                </h4>

                <Confetti
                    width={document.body.clientWidth}
                    height={document.body.scrollHeight}
                    initialVelocityY={30}
                    initialVelocityX={20}
                    numberOfPieces={300}
                    recycle={false}
                    confettiSource={{
                        /* eslint-disable id-length */
                        x: 0,
                        y: document.body.scrollHeight,
                        w: 0,
                        h: 0,
                        /* eslint-enable id-length */
                    }}
                />
                <Confetti
                    width={document.body.clientWidth}
                    height={document.body.scrollHeight}
                    initialVelocityY={30}
                    initialVelocityX={-20}
                    numberOfPieces={300}
                    recycle={false}
                    confettiSource={{
                        /* eslint-disable id-length */
                        x: document.body.clientWidth,
                        y: document.body.scrollHeight,
                        w: 0,
                        h: 0,
                        /* eslint-enable id-length */
                    }}
                />

                <div className="text-center">
                    You can now start using the full power of the platform.
                </div>

            </div>

        </div>
    );
}

const DURATION_TO_NAME = {
    [PortalBillingTierDuration.Month]: {
        text: "Monthly",
        priceText: "per month",
    },
    [PortalBillingTierDuration.Year]: {
        text: "Annual",
        priceText: "per year",
    },
};

export default function BillingSettings(): ReactElement {
    useArticleContext("urn:fq:article:67d9c5ea6d6c4cbd8ec38dcd75459521");
    const [duration, setDuration] = useState<PortalBillingTierDuration>(PortalBillingTierDuration.Month);
    const [seats, setSeats] = useState<number>(0);

    const { data, loading: isLoading } = useGetBillingQuery();

    const [createOrder] = useCreateBillingOrderMutation();

    useEffect(() => {
        if (!data?.billing?.tiers) {
            return undefined;
        }
        const script = document.createElement("script");
        script.src = "https://js.stripe.com/v3/pricing-table.js";
        script.nonce = window.NONCE;
        script.async = true;
        document.body.appendChild(script);
        return () => {
            document.body.removeChild(script);
        };
    }, []);

    const subscribe = async (encryptedPriceId: string) => {
        const order = await createOrder({
            variables: {
                order: {
                    lines: [
                        {
                            encryptedPriceId,
                            quantity: seats,
                        },
                    ],
                },
            },
        });
        if (order.data?.billingOrder?.redirectUrl) {
            window.location.href = order.data.billingOrder.redirectUrl;
        }
    };

    useEffect(() => {
        if (data?.billing?.defaultQuantity) {
            setSeats(data?.billing.defaultQuantity);
        }
    }, [data?.billing]);

    if (data?.billing?.redirectUrl) {
        window.location.href = data?.billing?.redirectUrl;
        // eslint-disable-next-line react/jsx-no-useless-fragment
        return <></>;
    }

    if (isLoading) {
        return <LoadingIcon />;
    }
    return (

        <div className="border p-5 rounded bg-white">

            { data?.billing?.tiers ? (
                <div>
                    <div className="d-lg-flex gap-3 mb-5">
                        <div className="col-lg border-lg p-lg-3 rounded">
                            <h4>
                                1. Number of Agents
                            </h4>
                            <div className="d-flex justify-content-start mb-4 ps-4">
                                <FormGroup
                                    type="number"
                                    onChange={event => setSeats(parseInt(event.target.value, 10))}
                                    value={seats}
                                    className="fs-4"
                                    min={1}
                                    helpText={(
                                        <>
                                            How many agents do you need to start with?
                                            {" "}
                                            <Popover title="What is an agent?">
                                                An agent license is required for each user getting value
                                                from Foqal. Often the easiest
                                                way to understand if a user is an agent or not is
                                                if they are able to close a ticket.
                                                If a user is closing a ticket either in Foqal directly
                                                or using a third party tool like
                                                Zendesk to close a ticket, this is most likely an agent.
                                                If you have any questions
                                                about whether a user is an agent or not, please chat with support.
                                            </Popover>
                                        </>
                                    )}
                                />
                            </div>
                        </div>
                        <div className="col-lg border-lg p-lg-3 rounded">
                            <h4>2. Choose your Contract Period</h4>
                            <div className="d-flex ms-4">
                                <ButtonGroup className="">
                                    {Object.entries(DURATION_TO_NAME).map(([value, text]) => (
                                        <Button
                                            onClick={() => setDuration(value as PortalBillingTierDuration)}
                                            variant="none"
                                            className={duration === value
                                                ? "btn-primary"
                                                : "btn-outline-primary border border-primary"}
                                        >
                                            {text.text}
                                        </Button>
                                    ))}
                                </ButtonGroup>
                            </div>
                        </div>
                    </div>

                    <h3 className="mb-3">3. Choose your Plan</h3>

                    <div className="d-lg-flex gap-3">
                        {data?.billing?.tiers?.map(tier => {
                            const price = tier.prices.find(current => current.duration === duration);
                            if (!price) {
                                return null;
                            }
                            const durationName = DURATION_TO_NAME[price.duration];
                            const formatter = new Intl.NumberFormat(undefined, {
                                style: "currency",
                                currency: price.currency,
                                minimumFractionDigits: 0,
                            });

                            const isEnabled = price.minimumNumberOfUsers <= seats;
                            return (
                                <div className={classNames(
                                    "col d-flex flex-column border rounded p-3",
                                    price.isRecommended ? " shadow" : "",
                                )}
                                >

                                    <h4 className="d-flex align-items-center w-100 justify-content-between">
                                        {tier.name}
                                        {price.isRecommended && (
                                            <small className="text-light fs-6 ms-3">
                                                <RiVerifiedBadgeLine />
                                                {" "}
                                                Most Popular
                                            </small>
                                        )}
                                    </h4>
                                    <div className="flex-grow-1">
                                        <p>{tier.description}</p>
                                        <h5>Benefits</h5>
                                        <ul className="list-unstyled ms-2">
                                            {tier.benefits.map(benefit => (
                                                <li>
                                                    <RiCheckboxCircleFill />
                                                    {" "}
                                                    {benefit}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    <div className="d-flex gap-2">
                                        <h1>
                                            {formatter.format(price.price / 100)}
                                        </h1>
                                        <small className="text-light">
                                            / per agent
                                            {" "}
                                            <br />
                                            /
                                            {" "}
                                            {durationName.priceText}
                                        </small>
                                    </div>

                                    <button
                                        className="fs-5 btn btn-primary mt-2"
                                        type="button"
                                        onClick={() => subscribe(price.encryptedPriceId)}
                                        disabled={!isEnabled}
                                    >
                                        {isEnabled ? "Subscribe" : `Minimum ${price.minimumNumberOfUsers} agents`}
                                    </button>
                                    <div className="text-light mt-2 text-end">
                                        Total:
                                        {" "}
                                        {formatter.format((price.price * (seats ?? 1)) / 100)}
                                        {" "}
                                        {durationName.priceText}
                                    </div>

                                </div>
                            );
                        })}

                    </div>
                </div>
            ) : (
                <stripe-pricing-table
                    pricing-table-id={data?.billing?.pricingTableId}
                    publishable-key={data?.billing?.publishableKey}
                    client-reference-id={data?.billing?.sessionId}
                    customer-email={data?.billing?.currentUserEmail}
                />
            )}

        </div>
    );
}
